import { useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { useState } from "react";
import { sharedMutators } from "../../../../shared/mutators";
import { queries } from "../../../../shared/queries";
import { MULTI_IDP_SETTINGS_FLAG } from "../../../../shared/featureFlagNames";
import { useFeatureFlag } from "../../../hooks/useFeatureFlag";
import { useHasAccess } from "../../../hooks/useHasAccess";
import { useQuery } from "../../../hooks/useQuery";
import {
  makeUrl,
  navigate,
  routes,
  useCurrentUrl,
  useRouteMatch,
} from "../../../lib/urls";
import { Button } from "../../Button";
import { ButtonLink } from "../../Button/ButtonLink";
import { ConfirmModal } from "../../ConfirmModal";
import { Dropdown } from "../../Dropdown";
import { EmptyState } from "../../EmptyState";
import { Icon } from "../../Icon";
import { Link } from "../../Link";
import { ListTable } from "../../ListTable";
import { StatusBadge } from "../../StatusBadge";
import { Text } from "../../Text";
import { Tooltip } from "../../Tooltip";
import { Cell, IdentityCell } from "../IdentityCell";
import {
  PanelCard,
  PanelContentBox,
  PanelHeader,
  PanelShell,
} from "../SettingsPanel";
import { Container, Header, ScrollArea } from "../shared";
import { ExistingSsoProvider } from "./ExistingSsoProvider";
import { LegacySSOSettings } from "./LegacySSOSettings";
import { NewSsoProvider } from "./NewSsoProvider";
import { RevokeSessionsModal, ScimTokenModal } from "./SsoModals";

const SYNC_MODE_LABELS: Record<string, string> = {
  off: "Off",
  jit: "JIT",
  jit_users_and_groups: "JIT + groups",
  scim: "SCIM",
};

const BackLink = styled(Link)`
  display: flex;
  width: fit-content;
  align-items: center;
  gap: 6px;
  color: var(--text-secondary);
  text-decoration: none;
  margin-bottom: 8px;
`;

const SessionsFooter = styled("div")`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 24px;
  border-top: 0.5px solid var(--border-alpha);
`;

export function SSOSettings() {
  const multiIdp = useFeatureFlag(MULTI_IDP_SETTINGS_FLAG);
  return multiIdp ? <MultiIdpSSOSettings /> : <LegacySSOSettings />;
}

function MultiIdpSSOSettings() {
  const z = useZero();
  const [providers] = useQuery(queries.ssoProviders());
  const canManageSSO = useHasAccess("tenant:sso_manage");
  const { provider: selectedId } = useCurrentUrl(routes.settings.sso);
  const [isNew] = useRouteMatch(routes.settings.sso.new);
  // Lives here so a token minted while registering a provider survives
  // navigating back to the list.
  const [scimToken, setScimToken] = useState<string | null>(null);
  const [confirmRevoke, setConfirmRevoke] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<{
    id: string;
    providerId: string;
    isPrimary: boolean;
  } | null>(null);

  if (!canManageSSO) {
    return (
      <Container>
        <Header>
          <Text variant="text-regular-md">Single sign-on</Text>
        </Header>
        <ScrollArea>
          <div style={{ padding: 24 }}>
            <Text variant="text-regular" color="var(--text-secondary)">
              Only admins can configure SSO
            </Text>
          </div>
        </ScrollArea>
      </Container>
    );
  }

  const selected = providers.find((provider) => provider.id === selectedId);

  function backToList() {
    navigate(routes.settings.sso, { provider: "" }, { replace: true });
  }

  function handleDelete() {
    if (confirmDelete == null) return;
    z.mutate(sharedMutators.deleteSSOProvider({ id: confirmDelete.id }));
    setConfirmDelete(null);
    setScimToken(null);
  }

  const items = providers.map((provider) => ({
    key: provider.id,
    link: makeUrl(null, { provider: provider.id }),
    name: (
      <IdentityCell
        title={provider.providerId}
        subtitle={provider.issuer}
        icon={<Icon name="shield-check" size={16} />}
        badge={
          provider.isPrimary ? (
            <Tooltip content="Used for app sign-in">
              <StatusBadge color="green">Primary</StatusBadge>
            </Tooltip>
          ) : undefined
        }
      />
    ),
    protocol: <Cell value={provider.protocol.toUpperCase()} />,
    domain: <Cell value={provider.domain} />,
    syncMode: (
      <Cell value={SYNC_MODE_LABELS[provider.syncMode ?? "off"] ?? "Off"} />
    ),
    actions: (hasMouse: boolean) => {
      const menuItems = [
        ...(provider.isPrimary
          ? []
          : [
              {
                type: "button" as const,
                left: <Icon name="star" size={16} />,
                label: "Set as primary",
                onClick: () =>
                  z.mutate(
                    sharedMutators.setPrimarySSOProvider({ id: provider.id })
                  ),
              },
            ]),
        {
          type: "button" as const,
          left: <Icon name="trash" size={16} />,
          label: "Delete",
          onClick: () =>
            setConfirmDelete({
              id: provider.id,
              providerId: provider.providerId,
              isPrimary: provider.isPrimary ?? false,
            }),
        },
      ];
      return (
        <div
          style={{ opacity: hasMouse ? 1 : 0 }}
          onMouseDown={(event) => event.stopPropagation()}
        >
          <Dropdown
            opener={<Button variant="secondary" icon="more" />}
            align="bottom"
            minWidth={180}
            sections={[{ items: menuItems }]}
          />
        </div>
      );
    },
  }));

  const detail = isNew ? (
    <NewSsoProvider onGeneratedScimToken={setScimToken} onSaved={backToList} />
  ) : selected ? (
    <ExistingSsoProvider
      provider={selected}
      onGeneratedScimToken={setScimToken}
    />
  ) : null;

  const modals = (
    <>
      <ScimTokenModal token={scimToken} onClose={() => setScimToken(null)} />
      <ConfirmModal
        open={confirmDelete != null}
        onClose={() => setConfirmDelete(null)}
        onConfirm={handleDelete}
        title="Delete SSO provider"
        description={
          confirmDelete == null
            ? ""
            : `Users will no longer be able to sign in with “${confirmDelete.providerId}”.${
                confirmDelete.isPrimary
                  ? " App sign-in will use the built-in provider until another provider is set as primary."
                  : ""
              } This can’t be undone.`
        }
        confirmLabel="Delete"
        destructive
      />
      <RevokeSessionsModal
        open={confirmRevoke}
        onClose={() => setConfirmRevoke(false)}
      />
    </>
  );

  if (detail) {
    return (
      <PanelShell>
        <PanelCard>
          <PanelHeader>
            <BackLink to={makeUrl(routes.settings.sso, { provider: "" })}>
              <Icon name="chevron-left" size={12} />
              <Text variant="text-small">Single sign-on</Text>
            </BackLink>
            <Text variant="display-md">
              {isNew ? "Add provider" : (selected?.providerId ?? "")}
            </Text>
            {isNew && (
              <div style={{ marginTop: 8 }}>
                <Text variant="text-regular" color="var(--text-secondary)">
                  Group mappings sync roles and space memberships on each
                  sign-in.
                </Text>
              </div>
            )}
          </PanelHeader>
          <PanelContentBox>{detail}</PanelContentBox>
        </PanelCard>
        {modals}
      </PanelShell>
    );
  }

  return (
    <Container>
      <Header style={{ paddingRight: 12 }}>
        <Text variant="text-regular-md">Single sign-on</Text>
        <div style={{ marginLeft: "auto" }}>
          <ButtonLink
            size="l"
            icon="plus"
            to={makeUrl(routes.settings.sso.new, {})}
          />
        </div>
      </Header>

      <ScrollArea>
        {providers.length === 0 ? (
          <EmptyState
            iconName="shield-check"
            title="No SSO providers"
            description="Register an identity provider to let your users sign in with SSO."
          />
        ) : (
          <ListTable
            headers={{
              name: "Provider",
              protocol: "Protocol",
              domain: "Domain",
              syncMode: "Provisioning",
              actions: "",
            }}
            items={items}
          />
        )}
      </ScrollArea>

      <SessionsFooter>
        <div>
          <Text variant="text-regular-md">Sessions</Text>
          <div style={{ marginTop: 4 }}>
            <Text variant="text-regular" color="var(--text-secondary)">
              Force all users to re-authenticate
            </Text>
          </div>
        </div>
        <Button
          variant="subtle"
          size="l"
          onClick={() => setConfirmRevoke(true)}
        >
          Revoke all sessions
        </Button>
      </SessionsFooter>

      {modals}
    </Container>
  );
}
